import { HudButton } from 'components/HudButton'
import { usePointCloudContext } from 'features/pointClouds'
import { HelpCircle, MousePointer } from 'lucide-react'
import React from 'react'
import styled, { keyframes } from 'styled-components'

const fadeInMini = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 0.95;
    transform: translateY(0);
  }
`

const MiniWASDOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.6);
  padding: 36px;
  border-radius: 8px;
  color: white;
  text-align: center;
  z-index: 2000;
  animation: ${fadeInMini} 0.3s ease-out forwards;
  font-size: 10px;
  opacity: 0.95;
  transition: opacity 0.2s ease;
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: default;
  display: flex;
  gap: 24px;
  align-items: flex-start;
`

const ControlColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`

const ControlSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`

const ControlLabel = styled.div`
  font-size: 10px;
  opacity: 0.95;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const ControlKey = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  padding: 4px 8px;
  font-weight: bold;
  font-size: 12px;
  min-width: 24px;
  text-align: center;
`

const SpeedIndicators = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 4px;
  font-size: 12px;
`

const SpeedLevel = styled.span<{ isActive: boolean }>`
  color: ${({ isActive }) => (isActive ? '#fff' : 'rgba(255, 255, 255, 0.5)')};
  font-weight: ${({ isActive }) => (isActive ? 'bold' : 'normal')};
`

const ControlKeyRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const MiniWASDKeys = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 4px;
  margin-top: 4px;
  width: 100px;
`

const MiniKey = styled.div<{ gridCol: number; gridRow: number }>`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  padding: 6px;
  text-align: center;
  font-weight: bold;
  font-size: 12px;
  grid-column: ${({ gridCol }) => gridCol};
  grid-row: ${({ gridRow }) => gridRow};
`

const InstructionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
`

const InstructionRowWithMargin = styled(InstructionRow)`
  margin-top: 14px;
`

const HelpButtonWrapper = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2002;
  pointer-events: all;

  /* Override HudButton styles */

  > div {
    background-color: rgba(0, 0, 0, 0.6);
    color: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(255, 255, 255, 0.3);
    backdrop-filter: blur(4px);

    &:hover {
      background-color: rgba(0, 0, 0, 0.8);
    }
  }

  > div[data-selected='true'] {
    background-color: rgba(255, 255, 255, 0.9);
    color: rgba(0, 0, 0, 0.8);

    &:hover {
      background-color: rgba(255, 255, 255, 1);
    }
  }
`

interface MiniInstructionsOverlayProps {
  isVisible: boolean
}

const MouseIcon = styled(MousePointer)`
  width: 16px;
  height: 16px;
  opacity: 0.8;
`
export const MiniInstructionsOverlay: React.FC<
  MiniInstructionsOverlayProps
> = ({ isVisible }) => {
  const [isOpen, setIsOpen] = React.useState(true)
  const { speedBoostLevel } = usePointCloudContext()

  if (!isVisible) return null

  return (
    <>
      <HelpButtonWrapper>
        <HudButton
          icon={<HelpCircle />}
          isSelected={isOpen}
          onClick={() => setIsOpen(!isOpen)}
          animation='scale'
          scaleAmount={1.15}
          hasBorder={false}
          small={true}
        />
      </HelpButtonWrapper>
      {isOpen && (
        <MiniWASDOverlay>
          <ControlColumn>
            <ControlSection>
              <ControlLabel>Movement</ControlLabel>
              <MiniWASDKeys>
                <MiniKey gridCol={2} gridRow={1}>
                  W
                </MiniKey>
                <MiniKey gridCol={1} gridRow={2}>
                  A
                </MiniKey>
                <MiniKey gridCol={2} gridRow={2}>
                  S
                </MiniKey>
                <MiniKey gridCol={3} gridRow={2}>
                  D
                </MiniKey>
              </MiniWASDKeys>
              <InstructionRow>
                <ControlLabel>then</ControlLabel>
                <MouseIcon style={{ marginRight: 2 }} />
                <ControlLabel>
                  <b>Click</b> to enter viewer
                </ControlLabel>
              </InstructionRow>
              <InstructionRow>
                <ControlKeyRow>
                  <ControlKey>Space</ControlKey>
                  <ControlLabel>Up</ControlLabel>
                </ControlKeyRow>
                <ControlKeyRow>
                  <ControlKey>Shift</ControlKey>
                  <ControlLabel>Down</ControlLabel>
                </ControlKeyRow>
              </InstructionRow>
              <InstructionRowWithMargin>
                <MouseIcon style={{ marginRight: 2 }} />
                <ControlLabel>
                  <b>Click + Drag</b> for Orbit controls
                </ControlLabel>
              </InstructionRowWithMargin>
            </ControlSection>
          </ControlColumn>
          <ControlColumn>
            <ControlSection>
              <ControlLabel>Toggle Speed Up</ControlLabel>
              <ControlKey>Tab</ControlKey>
              <SpeedIndicators>
                <SpeedLevel isActive={speedBoostLevel === 0}>1</SpeedLevel>
                <SpeedLevel isActive={speedBoostLevel === 1}>2</SpeedLevel>
                <SpeedLevel isActive={speedBoostLevel === 2}>3</SpeedLevel>
                <SpeedLevel isActive={speedBoostLevel === 3}>4</SpeedLevel>
              </SpeedIndicators>
            </ControlSection>
            <ControlSection>
              <ControlLabel>Free Mouse</ControlLabel>
              <ControlKeyRow>
                <ControlKey>E</ControlKey>
                <ControlKey>Esc</ControlKey>
              </ControlKeyRow>
            </ControlSection>
            <ControlSection>
              <ControlLabel>Add Observation</ControlLabel>
              <ControlKey>R</ControlKey>
            </ControlSection>
          </ControlColumn>
        </MiniWASDOverlay>
      )}
    </>
  )
}
